import { useEffect, useMemo, useState } from 'react';
import { Package, Plus, Trash2, Save, Loader2, Sparkles, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import type { Job } from '../lib/store';

interface Consumable {
  id: string;
  name: string;
  unit: string;
  default_price: number;
  source?: string;
}

interface UsedItem {
  consumable_id: string;
  name: string;
  quantity: number;
  unit: string;
  unit_price?: number;
}

export default function MaterialsUsedSection({
  job,
  onSaved,
}: {
  job: Job;
  onSaved: () => void;
}) {
  const [inventory, setInventory] = useState<Consumable[]>([]);
  const [items, setItems] = useState<UsedItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiParsing, setAiParsing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/consumables');
        setInventory(res.data || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    setItems(
      (job.consumables || []).map((c: any) => ({
        consumable_id: c.consumable_id,
        name: c.name,
        quantity: c.quantity,
        unit: c.unit,
        unit_price: c.unit_price,
      }))
    );
    setDirty(false);
  }, [job.id]);

  const inventoryByName = useMemo(() => {
    const m: Record<string, Consumable> = {};
    inventory.forEach((c) => (m[c.name.toLowerCase()] = c));
    return m;
  }, [inventory]);

  const addFromInventory = (c: Consumable) => {
    setItems((arr) => [
      ...arr,
      {
        consumable_id: c.id,
        name: c.name,
        quantity: 1,
        unit: c.unit,
        unit_price: c.default_price,
      },
    ]);
    setDirty(true);
    setPickerOpen(false);
  };

  const addCustom = () => {
    setItems((arr) => [
      ...arr,
      { consumable_id: '', name: '', quantity: 1, unit: 'each' },
    ]);
    setDirty(true);
  };

  const runAiParse = async () => {
    if (!aiText.trim()) return;
    setAiParsing(true);
    try {
      const res = await api.post('/ai/parse-materials', { description: aiText });
      const parsed = res.data?.items || [];
      const newItems: UsedItem[] = parsed.map((it: any) => ({
        consumable_id: it.consumable_id || '',
        name: it.name,
        quantity: it.quantity,
        unit: it.unit || 'each',
        unit_price: it.unit_price ?? undefined,
      }));
      setItems((arr) => [...arr, ...newItems]);
      setDirty(true);
      setAiOpen(false);
      setAiText('');
      toast.success(`Added ${newItems.length} item${newItems.length === 1 ? '' : 's'} from AI`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'AI parse failed');
    } finally {
      setAiParsing(false);
    }
  };

  const update = (i: number, patch: Partial<UsedItem>) => {
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    setDirty(true);
  };

  const remove = (i: number) => {
    setItems((arr) => arr.filter((_, idx) => idx !== i));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Ensure any custom items exist as consumables (idempotent server-side)
      const finalItems: UsedItem[] = [];
      for (const it of items) {
        if (!it.name.trim()) continue;
        let id = it.consumable_id;
        if (!id) {
          // Try to match by name against inventory first
          const match = inventoryByName[it.name.trim().toLowerCase()];
          if (match) {
            id = match.id;
          } else {
            try {
              const created = await api.post('/consumables', {
                name: it.name.trim(),
                unit: it.unit,
                default_price: it.unit_price || 0,
              });
              id = created.data.id;
            } catch (e) {
              id = 'custom';
            }
          }
        }
        finalItems.push({
          consumable_id: id,
          name: it.name.trim(),
          quantity: Number(it.quantity),
          unit: it.unit,
          unit_price: it.unit_price,
        });
      }
      await api.put(`/jobs/${job.id}`, { consumables: finalItems });
      toast.success('Materials saved');
      setDirty(false);
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to save materials');
    } finally {
      setSaving(false);
    }
  };

  const total = items.reduce(
    (s, it) => s + (it.unit_price || 0) * (it.quantity || 0),
    0
  );

  return (
    <div className="card p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">
            Materials used · {items.length}
          </h3>
        </div>
        {total > 0 && (
          <div className="text-xs text-slate-500">
            Estimated total{' '}
            <span className="font-mono font-bold text-slate-900">£{total.toFixed(2)}</span>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">
          No materials added yet.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div
              key={i}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg"
            >
              <input
                className="input flex-1"
                list="mat-sugg"
                placeholder="Material name"
                value={it.name}
                onChange={(e) => {
                  const val = e.target.value;
                  const match = inventoryByName[val.toLowerCase()];
                  update(i, {
                    name: val,
                    ...(match
                      ? {
                          consumable_id: match.id,
                          unit: match.unit,
                          unit_price: match.default_price,
                        }
                      : {}),
                  });
                }}
              />
              <input
                type="number"
                min={0}
                step={0.5}
                className="input w-20"
                value={it.quantity}
                onChange={(e) => update(i, { quantity: Number(e.target.value) })}
              />
              <input
                className="input w-24"
                value={it.unit}
                onChange={(e) => update(i, { unit: e.target.value })}
                placeholder="unit"
              />
              <div className="relative w-24">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                  £
                </span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  className="input pl-6"
                  value={it.unit_price ?? ''}
                  onChange={(e) =>
                    update(i, {
                      unit_price: e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                  placeholder="price"
                />
              </div>
              <button
                onClick={() => remove(i)}
                className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <datalist id="mat-sugg">
        {inventory.slice(0, 400).map((c) => (
          <option key={c.id} value={c.name} />
        ))}
      </datalist>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button
          onClick={() => setAiOpen(true)}
          type="button"
          className="btn text-sm bg-purple-100 text-purple-800 hover:bg-purple-200"
        >
          <Wand2 className="h-3.5 w-3.5" /> AI parse
        </button>
        <button
          onClick={() => setPickerOpen(true)}
          className="btn btn-secondary text-sm"
        >
          <Sparkles className="h-3.5 w-3.5" /> From inventory
        </button>
        <button onClick={addCustom} className="btn btn-secondary text-sm">
          <Plus className="h-3.5 w-3.5" /> Add custom
        </button>
        {dirty && (
          <button
            onClick={save}
            disabled={saving}
            className="btn btn-primary text-sm ml-auto"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save materials
          </button>
        )}
      </div>

      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setAiOpen(false)} />
          <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-pop overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center gap-2 mb-1">
                <Wand2 className="h-5 w-5 text-purple-600" />
                <h3 className="font-bold text-slate-900">AI parse materials</h3>
              </div>
              <p className="text-xs text-slate-500">
                Type in plain English — AI will match against your Xero-learned inventory.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <textarea
                autoFocus
                className="input min-h-[120px] resize-y"
                placeholder="e.g. Used 2m of 15mm copper pipe, 3 elbows, 1 isolation valve, 1 tube PTFE tape"
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setAiOpen(false)} className="btn btn-secondary" disabled={aiParsing}>
                  Cancel
                </button>
                <button onClick={runAiParse} disabled={aiParsing || !aiText.trim()} className="btn btn-primary">
                  {aiParsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  Parse & add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pickerOpen && (
        <InventoryPicker
          inventory={inventory}
          onPick={addFromInventory}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function InventoryPicker({
  inventory,
  onPick,
  onClose,
}: {
  inventory: Consumable[];
  onPick: (c: Consumable) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return inventory.slice(0, 100);
    return inventory
      .filter((c) => c.name.toLowerCase().includes(ql))
      .slice(0, 100);
  }, [inventory, q]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-pop overflow-hidden max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-slate-100">
          <input
            autoFocus
            className="input"
            placeholder="Search inventory…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">No parts match.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onPick(c)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500">
                      {c.unit}
                      {c.source === 'xero_learned' && ' · Xero'}
                    </div>
                  </div>
                  <div className="font-mono font-semibold text-slate-900 text-sm">
                    £{c.default_price?.toFixed(2) || '0.00'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
